import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { styleMap } from 'lit/directives/style-map.js';
import { resolveColor } from '../helper/utils/color-resolver';
import skyLocalSetData from './ion-local.json';
import { PresetController } from "../helper/controllers/preset-controller";

export type SkyIconSizeValue = string | number;
export type SkyIconRotateValue = string | number;
export type SkyIconFlip =
  '' | 'horizontal' | 'vertical' | 'horizontal,vertical';

interface IconifyCollection {
  left?: number;
  top?: number;
  width?: number;
  height?: number;
}

interface IconifySetData extends IconifyCollection {
  icons?: Record<string, IconifyIcon>;
}

interface IconifyIcon {
  body?: string;
  left?: number;
  top?: number;
  width?: number;
  height?: number;
  viewBox?: string;
}

const MAX_ICON_CACHE_SIZE = 100;

/**
 * @element sky-icon
 *
 * @summary Icon renderer with Iconify fetch/caching, lazy loading, and optional slotted SVG fallback.
 *
 * @status stable
 * @since 1.0.0
 *
 * @documentation https://sky-ui.com/components/icon
 *
 * @slot - Optional fallback `<svg>` content when remote icon data is unavailable.
 * @property {string} icon - Icon name in `prefix:icon-name` format.
 * @property {SkyIconSizeValue} width - Icon width value.
 * @property {SkyIconSizeValue} height - Icon height value.
 * @property {string} color - Icon color value.
 * @property {boolean} inline - Applies inline alignment behavior for text contexts.
 * @property {SkyIconFlip} flip - Flip transform descriptor.
 * @property {boolean} hFlip - Horizontal flip shortcut.
 * @property {boolean} vFlip - Vertical flip shortcut.
 * @property {SkyIconRotateValue} rotate - Rotation descriptor.
 * @property {boolean} lazy - Defers loading until the icon is visible.
 *
 * @method registerLocalSet Registers a local Iconify set for offline/custom usage.
 * @method registerLocalIcon Registers a single local icon override.
 *
 * @csspart icon - The icon container element.
 * @csspart svg - The rendered SVG element.
 *
 * @Behavior
 * - Resolves icons from local registrations first, then Iconify API.
 * - Uses bounded in-memory caching for fetched SVG payloads.
 * - Supports custom slotted SVG fallback when external fetch fails or is blocked.
 *
 * @example
 * ```html
 * <sky-icon icon="ion:home" width="24" height="24" color="#3b82f6"></sky-icon>
 * ```
 * ```vue
 * <template>
 *   <sky-icon icon="ion:home" width="24" height="24" color="#3b82f6"></sky-icon>
 * </template>
 * ```
 * ```jsx
 * export default function Demo() {
 *   return <sky-icon icon="ion:home" width="24" height="24" color="#3b82f6"></sky-icon>;
 * }
 * ```
 */
@customElement('sky-icon')
export class SkyIcon extends LitElement {
  /** Cache: icon name -> final SVG string (bounded by MAX_ICON_CACHE_SIZE, LRU eviction). */
  private static _iconCache = new Map<string, string>();

  /** Cache for in-flight fetches: icon name -> Promise<svg> */
  private static _iconPromiseCache = new Map<string, Promise<string>>();

  /** Local icon sets (prefix -> Iconify set data) for offline/custom icons */
  private static _localSets = new Map<string, IconifySetData>();

  /** Single local icons (full name -> icon data) */
  private static _localIcons = new Map<string, IconifyIcon>();

  /**
   * Register a local icon set (Iconify-compatible JSON) for offline or custom icons.
   * @param prefix - Set prefix (e.g. "ion", "custom"); use as icon="prefix:iconName".
   * @param data - Set data with `icons` and optional defaults (width, height, left, top).
   */
  static registerLocalSet(prefix: string, data: IconifySetData): void {
    SkyIcon._localSets.set(prefix, data);
    for (const key of SkyIcon._iconCache.keys()) {
      if (key.startsWith(prefix + ':')) SkyIcon._iconCache.delete(key);
    }
  }

  /**
   * Register a single local icon (one-off custom icon without a full set).
   * @param fullName - Full icon name, e.g. "ion:heart".
   * @param icon - Icon data (body, width, height, viewBox, etc.).
   */
  static registerLocalIcon(fullName: string, icon: IconifyIcon): void {
    SkyIcon._localIcons.set(fullName, icon);
    SkyIcon._iconCache.delete(fullName);
  }


  /** Named prop preset from nearest `sky-config-provider`. */
  @property({ type: String, reflect: true }) preset = "";

  private _presets = new PresetController(this);

  static styles = css`
    :host {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      vertical-align: middle;
    }
    .icon {
      display: flex;
      align-items: center;
      justify-content: center;
      line-height: 1;
    }
    .icon svg {
      display: block;
      width: 100%;
      height: 100%;
      flex-shrink: 0;
    }
    /* Custom SVG in slot: inherit size and color */
    .icon::slotted(svg) {
      display: block;
      width: 100%;
      height: 100%;
      flex-shrink: 0;
      fill: currentColor;
    }
  `;

  // Iconify-style public props

  @property({ type: String })
  icon = '';

  @property({ type: String })
  width: SkyIconSizeValue = '';

  @property({ type: String })
  height: SkyIconSizeValue = '';

  @property({ type: String })
  color = 'currentColor';

  @property({ type: Boolean })
  inline = false;

  @property({ type: String })
  flip: SkyIconFlip = '';

  @property({ type: Boolean })
  hFlip = false;

  @property({ type: Boolean })
  vFlip = false;

  @property({ type: String })
  rotate: SkyIconRotateValue = 0;

  @property({ type: Boolean })
  lazy = false;

  // Internal state

  @state()
  private _svgContent: string | null = null;

  @state()
  private _hasSlottedSvg = false;

  private _observer: IntersectionObserver | null = null;
  private _visible = false;

  // Lifecycle

  override connectedCallback(): void {
    super.connectedCallback();

    if (this.lazy) {
      this._startObserver();
    } else {
      this._visible = true;
      if (this.icon && !this._hasSlottedSvg) {
        this._loadIcon();
      }
    }
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this._stopObserver();
  }

  override updated(changed: Map<string, unknown>): void {
    // Lazy flag changed: reconfigure observer / visibility
    if (changed.has('lazy')) {
      if (this.lazy) {
        this._startObserver();
      } else {
        this._stopObserver();
        this._visible = true;
        if (this.icon && !this._hasSlottedSvg) {
          this._loadIcon();
        }
      }
    }

    // Icon changed: reload if visible and no custom SVG, otherwise clear
    if (changed.has('icon')) {
      if (this._hasSlottedSvg) {
        this._setSvgContent(null);
      } else if (!this.lazy || this._visible) {
        this._loadIcon();
      } else {
        this._setSvgContent(null);
      }
    }
  }

  private _onSlotChange(e: Event): void {
    const slot = e.target as HTMLSlotElement;
    const nodes = slot.assignedNodes({ flatten: true });
    const hasSvg = nodes.some(
      (n) =>
        n.nodeType === Node.ELEMENT_NODE && (n as Element).tagName === 'SVG'
    );
    if (this._hasSlottedSvg !== hasSvg) {
      this._hasSlottedSvg = hasSvg;
      if (hasSvg && this.icon) {
        this._setSvgContent(null);
      } else if (!hasSvg && this.icon && (this._visible || !this.lazy)) {
        this._loadIcon();
      }
    }
  }

  // Lazy loading

  private _startObserver(): void {
    if (typeof window === 'undefined') return;
    if (this._observer || !('IntersectionObserver' in window)) return;

    this._observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        this._visible = true;
        if (!this._hasSlottedSvg && this.icon) {
          this._loadIcon();
        }
        this._stopObserver();
      }
    });

    this._observer.observe(this);
  }

  private _stopObserver(): void {
    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }
  }

  // State setter (avoids nested update warning)

  private _setSvgContent(svg: string | null): void {
    // Avoid unnecessary updates
    if (this._svgContent === svg) return;

    // Schedule after current update cycle
    if (typeof queueMicrotask === 'function') {
      queueMicrotask(() => {
        this._svgContent = svg;
      });
    } else {
      // Fallback for environments without queueMicrotask
      Promise.resolve().then(() => {
        this._svgContent = svg;
      });
    }
  }

  // Icon loading

  private async _loadIcon(): Promise<void> {
    const name = this.icon.trim();
    if (!name) {
      this._setSvgContent(null);
      return;
    }

    // Cached SVG → still set via microtask to avoid nested update warning
    const cached = SkyIcon._iconCache.get(name);
    if (cached) {
      this._setSvgContent(cached);
      return;
    }

    let promise = SkyIcon._iconPromiseCache.get(name);
    if (!promise) {
      promise = this._fetchAndBuild(name);
      SkyIcon._iconPromiseCache.set(name, promise);
    }

    try {
      const svg = await promise;
      this._setSvgContent(svg);
      if (SkyIcon._iconCache.size >= MAX_ICON_CACHE_SIZE) {
        const firstKey = SkyIcon._iconCache.keys().next().value;
        if (firstKey != null) SkyIcon._iconCache.delete(firstKey);
      }
      SkyIcon._iconCache.set(name, svg);
    } catch {
      this._setSvgContent(null);
    } finally {
      SkyIcon._iconPromiseCache.delete(name);
    }
  }

  private async _fetchAndBuild(full: string): Promise<string> {
    const [prefix, iconName] = full.split(':');
    if (!prefix || !iconName) throw new Error('Invalid icon name');

    // 1) Single local icon (e.g. registerLocalIcon('ion:heart', {...}))
    const singleLocal = SkyIcon._localIcons.get(full);
    if (singleLocal) {
      return this._buildSvg(singleLocal, {});
    }

    // 2) Local set (e.g. registerLocalSet('ion', { icons: { home: {...} } }))
    const localSet = SkyIcon._localSets.get(prefix);
    if (localSet?.icons?.[iconName]) {
      return this._buildSvg(localSet.icons[iconName], localSet);
    }

    // 3) Fetch from Iconify API
    const url = `https://api.iconify.design/${prefix}.json?icons=${iconName}`;
    const res = await fetch(url);
    if (!res.ok) throw new Error('Failed to fetch icon set');

    const json = (await res.json()) as {
      icons?: Record<string, IconifyIcon>;
      left?: number;
      top?: number;
      width?: number;
      height?: number;
    };

    const data = json.icons?.[iconName];
    if (!data) throw new Error('Icon not found');

    return this._buildSvg(data, json);
  }

  private _buildSvg(icon: IconifyIcon, set: IconifyCollection): string {
    const body = icon.body ?? '';
    const l = icon.left ?? set.left ?? 0;
    const t = icon.top ?? set.top ?? 0;
    const w = icon.width ?? set.width ?? 24;
    const h = icon.height ?? set.height ?? 24;
    const viewBox = icon.viewBox || `${l} ${t} ${w} ${h}`;

    return `
      <svg xmlns="http://www.w3.org/2000/svg"
           viewBox="${viewBox}"
           width="100%"
           height="100%"
           preserveAspectRatio="xMidYMid meet"
           fill="currentColor">
        ${body}
      </svg>
    `;
  }

  // Style helpers

  private _normalize(v: string | number): string {
    if (typeof v === 'number') return `${v}px`;
    if (/^\d+$/.test(v)) return `${v}px`;
    return v;
  }

  private _rotationDeg(): number {
    const v = this.rotate;
    if (typeof v === 'number') return v * 90;
    if (typeof v === 'string') {
      if (v.endsWith('deg')) return parseFloat(v);
      const n = parseInt(v, 10);
      return Number.isFinite(n) ? n * 90 : 0;
    }
    return 0;
  }

  private _flipHas(val: 'horizontal' | 'vertical'): boolean {
    return this.flip
      .split(',')
      .map((v) => v.trim().toLowerCase())
      .includes(val);
  }

  private _transform(): string {
    const deg = this._rotationDeg();
    const h = this.hFlip || this._flipHas('horizontal');
    const v = this.vFlip || this._flipHas('vertical');

    const tr: string[] = [];
    if (deg) tr.push(`rotate(${deg}deg)`);
    if (h || v) tr.push(`scale(${h ? -1 : 1}, ${v ? -1 : 1})`);

    return tr.join(' ');
  }

  private _baseStyle(): Record<string, string> {
    const style: Record<string, string> = { color: resolveColor(this.color) };

    if (this.width) style.width = this._normalize(this.width);
    if (this.height) style.height = this._normalize(this.height);

    if (!this.width && !this.height) {
      style.width = '1em';
      style.height = '1em';
    }
    if (this.inline) style.verticalAlign = '-0.125em';

    return style;
  }

  // Render

  override render() {
    const style = this._baseStyle();
    const tf = this._transform();
    if (tf) style.transform = tf;

    const showIconify = !this._hasSlottedSvg && this.icon && this._svgContent;

    /* Iconify (API) path: same structure as before — single .icon div with innerHTML, no wrapper */
    if (showIconify) {
      return html`
        <div
          class="icon"
          style=${styleMap(style)}
          part="icon"
          .innerHTML=${this._svgContent!}
        ></div>
      `;
    }

    /* Custom SVG slot or empty: render slot for detection and display */
    return html`
      <div class="icon" style=${styleMap(style)} part="icon">
        <slot @slotchange=${this._onSlotChange}></slot>
      </div>
    `;
  }
}

// Register default local set (ion from ion-local.json); users can add more via SkyIcon.registerLocalSet / registerLocalIcon
const defaultSet = skyLocalSetData as IconifySetData;
if (defaultSet?.icons) {
  SkyIcon.registerLocalSet('ion', defaultSet);
}
